import { useState } from "react";
import {
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
} from "@mui/material";
import SbuxTextField from "../../../../components/SubxTextField";
import styles from "../styles";
import useCss from "../../../../hooks/useCss";
import {
  getNumberErrorMessage,
  getTextLengthErrorMessage,
  getDuplicateNameErrorMessage,
} from "../utils";
import { isNumeric } from "../utils";
import ClearIcon from "@mui/icons-material/Clear";
import { IconButton, TextField, Autocomplete } from "@mui/material";

export interface FieldData {
  label?: string;
  name?: string;
  id?: string;
  partitionKey?: boolean;
  inputMaxLength?: number;
  required?: boolean;
  readOnly?: boolean;
  selectValues?: string[];
  [key: string]: unknown;
}

export interface FormEntry {
  label?: string;
  value?: string;
  required?: boolean;
  isError?: boolean;
  errorMessage?: string;
  [key: string]: unknown;
}

interface Props {
  rows: Record<string, unknown>[];
  value: string;
  handleInputChange: (
    data: FieldData,
    value: string,
    formValues: Map<string, FormEntry>,
  ) => void;
  type?: string;
  required?: boolean;
  data?: FieldData;
  formValues: Map<string, FormEntry>;
  label?: string;
  readOnly?: boolean;
  preventAutoFocus?: boolean;
}

export const DataManagementCustomInput = ({
  rows,
  value,
  handleInputChange,
  type,
  required,
  data,
  formValues,
  label,
  readOnly,
  preventAutoFocus = false,
}: Props) => {
  const [inputValue, setInputValue] = useState(value);
  const [errorMessage, setErrorMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | null>(
    value ?? null,
  );
  const classes = useCss(styles);

  const checkDuplicate = (
    fieldData: FieldData | undefined,
    val: string,
  ): boolean => {
    if (fieldData?.partitionKey) {
      const index = rows.findIndex(
        (row) =>
          (row[fieldData?.name ?? ""] as string)?.toString()?.trim() ===
          val?.toString()?.trim(),
      );
      if (index >= 0) {
        return true;
      }
    }
    return false;
  };

  const validateInput = (
    fieldData: FieldData | undefined,
    val: string,
  ): string => {
    if (type === "number" && val && !isNumeric(val)) {
      return getNumberErrorMessage(fieldData?.label ?? "", type);
    }
    return "";
  };

  const handleCustomInputChange = (
    fieldData: FieldData | undefined,
    val: string,
  ) => {
    let localIsError = false;
    let localErrorMsg = "";
    setInputValue(val);

    localErrorMsg = validateInput(fieldData, val);
    if (localErrorMsg) {
      localIsError = true;
      setErrorMessage(localErrorMsg);
      setIsError(true);
    } else if (
      fieldData &&
      fieldData.inputMaxLength &&
      val.length > fieldData.inputMaxLength
    ) {
      localIsError = true;
      localErrorMsg = getTextLengthErrorMessage(fieldData.inputMaxLength);
      setErrorMessage(localErrorMsg);
      setIsError(true);
    } else if (checkDuplicate(fieldData, val)) {
      localIsError = true;
      localErrorMsg = getDuplicateNameErrorMessage(val);
      setErrorMessage(localErrorMsg);
      setIsError(true);
    } else {
      localIsError = false;
      setErrorMessage("");
      setIsError(false);
    }

    const newData: FieldData & {
      isError?: boolean;
      errorMessage?: string;
      value?: string;
    } = {
      ...fieldData,
      isError: localIsError,
      errorMessage: localErrorMsg,
    };
    if (val) {
      newData.value = val;
    }
    if (fieldData?.label) {
      formValues.set(fieldData.label, newData as FormEntry);
    }
    return handleInputChange(newData, val, formValues);
  };

  const handleAutoCompleteChange = (
    _event: React.SyntheticEvent,
    newValue: string | null,
  ) => {
    setSelectedValue(newValue);
    handleCustomInputChange(data, newValue ?? "");
    setErrorMessage("");
    setIsError(false);
  };

  const handleClearButtonClick = () => {
    setSelectedValue(null);
    handleCustomInputChange(data, "");
    setErrorMessage("");
    setIsError(false);
  };

  return (
    <Box>
      {type === "boolean" ? (
        <FormControl>
          <InputLabel>{label}</InputLabel>

          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={inputValue}
            onChange={(e) => {
              if (!data?.readOnly) {
                handleCustomInputChange(data, e.target.value);
              }
            }}
            className={readOnly ? classes.radioReadOnly : classes.radioEnabled}
          >
            <Box className={classes.radioGroup}>
              <FormControlLabel
                value="true"
                control={<Radio />}
                label={<Box className={classes.textField}>{"True"}</Box>}
              />
              <FormControlLabel
                value="false"
                control={<Radio />}
                label={<Box className={classes.textField}>{"False"}</Box>}
              />
            </Box>
          </RadioGroup>
        </FormControl>
      ) : data?.selectValues &&
        Array.isArray(data.selectValues) &&
        (type === "string" || type === "number") ? (
        <FormControl fullWidth>
          <Box>
            <Autocomplete
              size="small"
              options={data.selectValues}
              value={selectedValue}
              disabled={readOnly}
              onChange={handleAutoCompleteChange}
              renderInput={(params) => (
                <Box sx={{ position: "relative" }}>
                  <TextField
                    {...params}
                    label={label}
                    required={required}
                    error={isError}
                    helperText={errorMessage}
                    size="small"
                    autoFocus={!preventAutoFocus}
                  />
                  {selectedValue && !readOnly && (
                    <IconButton
                      className="clearButtonDataMgt"
                      disableFocusRipple
                      disableRipple
                      onClick={handleClearButtonClick}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                </Box>
              )}
            />
          </Box>
        </FormControl>
      ) : (
        <>
          <SbuxTextField
            className={`${
              readOnly ? classes.readOnlyInput : classes.enabledInput
            }`}
            label={label}
            variant={"outlined"}
            value={inputValue}
            handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              return handleCustomInputChange(data, e.target.value);
            }}
            errorMessage={errorMessage}
            isError={isError}
            readOnly={data?.readOnly || readOnly}
            displayCounter={!readOnly}
            maxLength={data?.inputMaxLength}
            required={data?.required}
            multiline={inputValue.length > 60}
            preventAutoFocus={preventAutoFocus}
          />
        </>
      )}
    </Box>
  );
};
